import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { KnxDeviceState } from '../version.service';
import { updatedAgo as fmtAgo } from '../shared/format.utils';
import { KnxSinceFormatter, MOBILE_VIEW_QUERY, PhoneMedia, groupByLocation, pct } from './knx-devices.utils';

type BlindState = 'open' | 'partial' | 'closed' | 'unknown';

/** Position at or below this counts as fully open, at or above `CLOSED_AT` as fully closed (KNX: 0 = up, 100 = down). */
const OPEN_AT   = 2;
const CLOSED_AT = 98;

const STATE_KEYS: Record<BlindState, string> = {
  open:    'KNX_BLIND_OPEN',
  partial: 'KNX_BLIND_PARTIAL',
  closed:  'KNX_BLIND_CLOSED',
  unknown: 'KNX_STATE_UNKNOWN',
};

interface BlindCard {
  id:        string;
  name:      string;
  floor:     string | null;
  room:      string | null;
  /** Rounded 0..100 % (0 = open); null when the HomeServer reported none. */
  position:  number | null;
  state:     BlindState;
  stateKey:  string;
  since:     string | null;
  sinceFull: string | null;
}

/** Blinds of one floor, rooms in HomeServer order (each card names its room). */
interface BlindFloor { key: string; floor: string | null; items: BlindCard[]; }

interface BlindSummary {
  total:   number;
  avgPos:  number | null;
  open:    number;
  partial: number;
  closed:  number;
}

/** Read-only KNX blinds: summary tiles and one window illustration per blind, closed down to its position. */
@Component({
  selector: 'app-knx-blinds',
  templateUrl: './knx-blinds.component.html',
  styles: [`
    :host {
      display: block;
      /* Window illustration (light theme); dark values below. */
      --knx-frame: #96a0aa; --knx-box: #6c757d; --knx-sill: #adb5bd;
      --knx-sky-top: #62adef; --knx-sky-bottom: #d3ebff; --knx-glare: rgba(255, 255, 255, .42);
      --knx-slat: #d6dbe0; --knx-slat-line: #9da6af; --knx-slat-edge: #5c656d;
    }
    :host-context([data-bs-theme="dark"]) {
      --knx-frame: #7b858f; --knx-box: #59626b; --knx-sill: #6c757d;
      --knx-sky-top: #163a66; --knx-sky-bottom: #3e76ad; --knx-glare: rgba(255, 255, 255, .1);
      --knx-slat: #9aa3ac; --knx-slat-line: #66707a; --knx-slat-edge: #d0d6dc;
    }
    .knx-tile-label { font-size: .68rem; letter-spacing: .04em; text-transform: uppercase; color: var(--bs-secondary-color); line-height: 1.2; }
    .knx-tile-value { font-size: 1.35rem; font-weight: 600; line-height: 1.15; font-variant-numeric: tabular-nums; }
    .knx-tile-sub   { font-size: .72rem; color: var(--bs-secondary-color); line-height: 1.25; }
    .knx-floor      { font-size: .65rem; letter-spacing: .06em; }
    .knx-hint       { font-size: .65rem; }

    /* Mini window swatch for the tiles: open / partly / closed */
    .knx-mini       { display: inline-block; width: .62rem; height: .74rem; margin-right: .32rem; vertical-align: -.1rem;
                      border: 1.5px solid var(--knx-frame); border-radius: 1px;
                      background: linear-gradient(to bottom, var(--knx-sky-top), var(--knx-sky-bottom)); }
    .knx-mini-partial { background: linear-gradient(to bottom, var(--knx-slat) 0 50%, var(--knx-slat-edge) 50% 60%, var(--knx-sky-bottom) 60%); }
    .knx-mini-closed  { background: var(--knx-slat); }

    /* Window: shutter box, frame with sky glass and centre mullion, shutter drawn down to the position, sill */
    .knx-win        { width: 100%; max-width: 92px; margin: .15rem auto .45rem; }
    .knx-win-box    { height: 8px; border-radius: 3px 3px 0 0; background: var(--knx-box); }
    .knx-win-frame  { aspect-ratio: 5 / 6; padding: 3px; background: var(--knx-frame); }
    .knx-win-glass  { position: relative; height: 100%; overflow: hidden; border-radius: 1px;
                      background: linear-gradient(to bottom, var(--knx-sky-top), var(--knx-sky-bottom)); }
    .knx-win-glass::before { content: ''; position: absolute; top: 0; bottom: 0; left: 50%; width: 3px; margin-left: -1.5px; background: var(--knx-frame); }
    .knx-win-glass::after  { content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none;
                      background: linear-gradient(135deg, var(--knx-glare) 0 16%, transparent 16% 27%, var(--knx-glare) 27% 31%, transparent 31%); }
    .knx-win-shutter { position: absolute; z-index: 2; top: 0; left: 0; right: 0; min-height: 3px;
                      background: repeating-linear-gradient(to top, var(--knx-slat) 0 4px, var(--knx-slat-line) 4px 5px);
                      border-bottom: 3px solid var(--knx-slat-edge); transition: height .6s ease; }
    .knx-win-sill   { height: 4px; margin: 0 -7%; border-radius: 2px; background: var(--knx-sill); }
    .knx-win-unknown .knx-win-glass { opacity: .45; }

    .knx-blind-name { font-size: .78rem; font-weight: 600; line-height: 1.2; }
    .knx-blind-room { font-size: .66rem; line-height: 1.2; color: var(--bs-secondary-color); }
    .knx-blind-pos  { margin-top: .2rem; line-height: 1.15; column-gap: .3rem; }
    .knx-blind-pct  { font-size: .95rem; font-weight: 600; font-variant-numeric: tabular-nums; }
    .knx-blind-state { font-size: .68rem; color: var(--bs-secondary-color); }
    .knx-since      { font-size: .62rem; line-height: 1.2; color: var(--bs-secondary-color); }
    .knx-pct-open    { color: var(--bs-success-text-emphasis); }
    .knx-pct-partial { color: var(--bs-warning-text-emphasis); }
    .knx-pct-closed  { color: var(--bs-secondary-color); }
    @media (min-width: 576px) { .knx-tile-value { font-size: 1.6rem; } }
    @media (min-width: 992px) { .knx-win { max-width: 112px; } }
    /* List rows of the mobile view: a 24 px window (thinner frame, box, slats and sill) and the position + state on the right */
    .knx-win-xs      { width: 24px; max-width: none; margin: 0; }
    .knx-win-xs .knx-win-box   { height: 3px; border-radius: 2px 2px 0 0; }
    .knx-win-xs .knx-win-frame { padding: 2px; }
    .knx-win-xs .knx-win-glass::before { width: 2px; margin-left: -1px; }
    .knx-win-xs .knx-win-glass::after  { display: none; }
    .knx-win-xs .knx-win-shutter { min-height: 2px; border-bottom-width: 2px;
                      background: repeating-linear-gradient(to top, var(--knx-slat) 0 2px, var(--knx-slat-line) 2px 3px); }
    .knx-win-xs .knx-win-sill  { height: 2px; margin: 0 -3px; }
    .knx-bend        { flex-direction: column; align-items: flex-end; gap: 0; line-height: 1.2; }
    /* Mobile view (< 768 px, landscape phones included): no text under 11 px */
    @media (max-width: 767.98px) {
      .knx-tile-label, .knx-hint, .knx-blind-state { font-size: .6875rem; }
      .knx-tile-sub   { font-size: .75rem; }
    }
  `],
})
export class KnxBlindsComponent implements OnChanges, OnDestroy {
  /** Blinds in backend location order; null while no snapshot arrived yet (skeletons). */
  @Input() devices:   KnxDeviceState[] | null = null;
  @Input() updatedAt: number | null = null;
  @Input() flash      = false;
  @Input() liveDotOn  = true;
  @Input() nowTick    = Date.now();
  @Input() darkMode   = false;

  summary: BlindSummary | null = null;
  floors:  BlindFloor[] = [];

  readonly skeletonTiles = [0, 1, 2, 3];
  readonly skeletonCards = [0, 1, 2, 3, 4, 5];
  /** Mobile view (< 768 px): one list row per blind instead of the window cards. */
  readonly listView = new PhoneMedia(undefined, MOBILE_VIEW_QUERY);

  /** UI language: argument of the pure `knxName` pipe (English room / floor / blind names unless German). */
  lang: string;
  private langSub: Subscription;

  constructor(readonly translate: TranslateService) {
    this.lang = translate.currentLang || translate.defaultLang || 'en';
    this.langSub = this.translate.onLangChange.subscribe((e: LangChangeEvent) => {
      this.lang = e.lang;
      this.build();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // nowTick / liveDotOn tick every second: only rebuild on a new snapshot.
    if (changes['devices']) this.build();
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
    this.listView.destroy();
  }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  trackFloor = (_: number, f: BlindFloor) => f.key;
  trackCard  = (_: number, b: BlindCard) => b.id;

  private build(): void {
    const devices = this.devices;
    if (!devices) { this.summary = null; this.floors = []; return; }
    const fmt = new KnxSinceFormatter();
    const cards = devices.map(d => KnxBlindsComponent.card(d, fmt));

    this.floors = groupByLocation(cards).map(f => ({
      key: f.key,
      floor: f.floor,
      items: f.rooms.reduce<BlindCard[]>((all, r) => all.concat(r.items), []),
    }));

    let sum = 0, known = 0, open = 0, partial = 0, closed = 0;
    for (const c of cards) {
      if (c.position == null) continue;
      sum += c.position;
      known++;
      if (c.state === 'open') open++;
      else if (c.state === 'closed') closed++;
      else partial++;
    }
    this.summary = { total: cards.length, avgPos: known ? Math.round(sum / known) : null, open, partial, closed };
  }

  private static card(d: KnxDeviceState, fmt: KnxSinceFormatter): BlindCard {
    const position = pct(d.position);
    const state: BlindState = position == null ? 'unknown'
      : position <= OPEN_AT ? 'open'
      : position >= CLOSED_AT ? 'closed'
      : 'partial';
    const since = typeof d.since === 'number' && d.since > 0 ? d.since : null;
    return {
      id: d.id,
      name: d.name,
      floor: d.floor ?? null,
      room: d.room ?? null,
      position,
      state,
      stateKey: STATE_KEYS[state],
      since: since != null ? fmt.short(since) : null,
      sinceFull: since != null ? fmt.long(since) : null,
    };
  }
}
